import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Bot, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import axios from "axios";
import { staggerContainer, staggerItem, hoverLift } from "@/config/animations";
import { formatDollars, gradeColor } from "@/lib/language";
import { ProviderIcon } from "@/components/logos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function gradeIcon(grade) {
  if (grade.startsWith("A")) return CheckCircle;
  if (grade.startsWith("B")) return CheckCircle;
  if (grade === "C") return AlertTriangle;
  return XCircle;
}

function gradeBg(grade) {
  if (grade.startsWith("A")) return "var(--qn-success-muted)";
  if (grade.startsWith("B")) return "var(--qn-warning-muted)";
  if (grade === "C") return "var(--qn-warning-muted)";
  return "var(--qn-error-muted)";
}

export default function AgentHealthCards({ viewMode }) {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/savings/agents`).then(r => setAgents(r.data)).catch(() => {});
  }, []);

  if (agents.length === 0) return null;

  return (
    <>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider">
          Agent Health
        </h3>
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="agent-health-cards"
          role="list"
          aria-label="Agent health grades"
        >
          {agents.slice(0, 8).map((a, i) => {
            const Icon = gradeIcon(a.grade);
            const color = gradeColor(a.grade);
            return (
              <motion.div key={a.agent_id} variants={staggerItem} whileHover={hoverLift.whileHover}>
                <Card
                  className="card-themed cursor-pointer transition-all hover:border-[var(--qn-border-hover)]"
                  style={{ borderLeftWidth: 3, borderLeftColor: color }}
                  onClick={() => setSelected(a)}
                  data-testid={`agent-card-${a.agent_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate">{a.name}</span>
                      <span className="text-lg font-bold" style={{ color }}>{a.grade}</span>
                    </div>
                    <div className="text-sm text-[var(--qn-foreground-muted)] font-mono mb-1">
                      {formatDollars(a.cost_per_task)}/task
                    </div>
                    <div className="flex items-center gap-1 text-xs" style={{ color }}>
                      <Icon className="w-3 h-3" />
                      <span className="truncate">{a.recommendation}</span>
                    </div>
                    {viewMode !== "money" && (
                      <div className="mt-2 text-xs text-[var(--qn-foreground-subtle)]">
                        {a.tasks_count} tasks | {a.efficiency}% efficient
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const name = (selected.name || "").toLowerCase();
                    const providerKey = name.includes("gpt") || name.includes("openai") ? "openai"
                      : name.includes("claude") || name.includes("anthropic") ? "anthropic"
                      : name.includes("gemini") || name.includes("google") ? "google"
                      : null;
                    return providerKey
                      ? <ProviderIcon providerKey={providerKey} size="sm" variant="color" />
                      : <Bot className="w-5 h-5" style={{ color: gradeColor(selected.grade) }} />;
                  })()}
                  {selected.name}
                  <Badge style={{ backgroundColor: gradeBg(selected.grade), color: gradeColor(selected.grade) }}>
                    Grade: {selected.grade}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[var(--qn-surface)] rounded-lg p-4">
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">Cost per Task</div>
                    <div className="text-xl font-mono font-bold">{formatDollars(selected.cost_per_task)}</div>
                  </div>
                  <div className="bg-[var(--qn-surface)] rounded-lg p-4">
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">Efficiency</div>
                    <div className="text-xl font-mono font-bold">{selected.efficiency}%</div>
                  </div>
                  <div className="bg-[var(--qn-surface)] rounded-lg p-4">
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">Tasks Completed</div>
                    <div className="text-xl font-mono font-bold">{selected.tasks_count}</div>
                  </div>
                  <div className="bg-[var(--qn-surface)] rounded-lg p-4">
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">Potential Savings</div>
                    <div className="text-xl font-mono font-bold text-[var(--qn-success)]">{formatDollars(selected.potential_saving)}/mo</div>
                  </div>
                </div>
                <div className="bg-[var(--qn-surface)] rounded-lg p-4">
                  <div className="text-sm font-medium mb-1">Recommendation</div>
                  <p className="text-sm text-[var(--qn-foreground-muted)]">{selected.recommendation}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
